import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useOrderQuery, useTissueQuery, useShipmentQuery, useUserBasicQuery } from "../queries";
import { displayDateAndTime, getUserBasicFullName } from "../helper-functions";
import { Claims, QueryType } from "../models";
import { usePermissions } from "../contexts";

interface UseCurrentShipmentParams {
    shipmentIdOverride?: number | string;
}

export function useCurrentShipment(params?: UseCurrentShipmentParams) {
    const { shipmentId: shipmentIdFromUrl } = useParams<{ shipmentId: string }>();
    const shipmentId = params?.shipmentIdOverride ?? shipmentIdFromUrl;
    const { hasPermission } = usePermissions();
    const {
        detailQuery: { isFetching, data: shipment },
    } = useShipmentQuery({ id: shipmentId });
    const {
        detailQuery: { data: order },
    } = useOrderQuery({ id: shipment?.orderId, queryType: QueryType.Detail });
    const tissueId = order?.tissues?.length ? order.tissues[0].id : undefined;
    const {
        detailQuery: { data: tissue },
    } = useTissueQuery({ id: tissueId, queryType: QueryType.Detail });
    const accountRequiresPoToShip = order?.shipment?.toAccount?.poRequiredToShip;
    const poNumber = tissue?.surgery?.poNumber;
    const missingPoNumber = accountRequiresPoToShip && !poNumber;
    const canPack = hasPermission(Claims.PackShipment) && !missingPoNumber;

    const userIds = useMemo(
        () => [shipment?.packedByUserId, shipment?.receivedByUserId].filter((id) => !!id).map((id) => Number(id)),
        [shipment]
    );
    const {
        listQuery: { data: users },
    } = useUserBasicQuery({ ids: userIds });

    const getUserName = (userId: number | undefined) => {
        const user = users?.find((u) => u.id === userId);
        return getUserBasicFullName(user);
    };

    const createdByName = getUserName(shipment?.createdById);
    const packedByName = getUserName(shipment?.packedByUserId);
    const receivedByName = getUserName(shipment?.receivedByUserId);

    const createdOn = displayDateAndTime(shipment?.createdOn);
    const shippedOn = displayDateAndTime(shipment?.shippedOn);
    const arrivedOn = displayDateAndTime(shipment?.arrivedOn);

    const isShipped = !!shipment?.shippedOn;

    return useMemo(
        () => ({
            isFetching,
            shipment,
            shipmentId,
            createdByName,
            packedByName,
            receivedByName,
            createdOn,
            shippedOn,
            arrivedOn,
            isShipped,
            poNumber,
            canPack,
            missingPoNumber,
        }),
        [
            isFetching,
            shipment,
            shipmentId,
            createdByName,
            packedByName,
            receivedByName,
            createdOn,
            shippedOn,
            arrivedOn,
            isShipped,
            poNumber,
            canPack,
            missingPoNumber,
        ]
    );
}
